import { useState, useEffect, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  NewspaperIcon,
  EyeIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import { toast } from 'react-hot-toast';
import MobilePressDetailsSheet from '../../components/MobilePressDetailsSheet';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';

interface PressRegistration {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  media_outlet: string;
  media_type: string;
  status: string;
  created_at: string;
  accreditation_type?: string;
  country?: string;
}

function statusColor(status: string) {
  switch (status) {
    case 'approved': return 'bg-green-100 text-green-700';
    case 'rejected': return 'bg-red-100 text-red-700';
    case 'pending': return 'bg-amber-100 text-amber-700';
    default: return 'bg-gray-100 text-gray-500';
  }
}

function statusIcon(status: string) {
  switch (status) {
    case 'approved': return <CheckCircleIcon className="w-4 h-4" />;
    case 'rejected': return <XCircleIcon className="w-4 h-4" />;
    default: return <ClockIcon className="w-4 h-4" />;
  }
}

export default function MobileAdminPress() {
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';
  const [registrations, setRegistrations] = useState<PressRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selected, setSelected] = useState<PressRegistration | null>(null);
  const [deleting, setDeleting] = useState<PressRegistration | null>(null);

  useEffect(() => { if (tournamentId) fetchRegistrations(); }, [tournamentId]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const query = supabase
        .from('press_registrations')
        .select('*')
        .order('created_at', { ascending: false });

      if (tournamentId) {
        query.eq('tournament_id', tournamentId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setRegistrations(data || []);
    } catch {
      toast.error('Failed to load press registrations');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => registrations.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = (r.full_name ?? '').toLowerCase().includes(q) ||
      (r.media_outlet ?? '').toLowerCase().includes(q) ||
      (r.email ?? '').toLowerCase().includes(q);
    const matchStatus = statusFilter === 'All' || (r.status ?? '').toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  }), [registrations, search, statusFilter]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      const { error } = await supabase.from('press_registrations').delete().eq('id', deleting.id);
      if (error) throw error;
      toast.success('Registration deleted');
      setDeleting(null);
      fetchRegistrations();
    } catch {
      toast.error('Failed to delete registration');
    }
  };

  const counts = {
    total: registrations.length,
    pending: registrations.filter(r => r.status === 'pending').length,
    approved: registrations.filter(r => r.status === 'approved').length,
    rejected: registrations.filter(r => r.status === 'rejected').length,
  };

  return (
    <div className="pb-4 space-y-4">
      {/* Stats */}
      <div className="px-4 pt-4 grid grid-cols-2 gap-3">
        {[
          { label: 'Total', value: counts.total, color: 'text-slate-600 bg-slate-50', icon: NewspaperIcon },
          { label: 'Pending', value: counts.pending, color: 'text-amber-600 bg-amber-50', icon: ClockIcon },
          { label: 'Approved', value: counts.approved, color: 'text-green-600 bg-green-50', icon: CheckCircleIcon },
          { label: 'Rejected', value: counts.rejected, color: 'text-red-500 bg-red-50', icon: XCircleIcon },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-lg font-bold text-golf-gray">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Sticky Search + Filters */}
      <div className="sticky top-14 z-10 bg-gray-50 px-4 pt-2 pb-3 space-y-2 border-b border-gray-100">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search press registrations..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-golf-orange bg-white"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-0.5 scrollbar-hide">
          {['All', 'Pending', 'Approved', 'Rejected'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                statusFilter === s ? 'bg-golf-orange text-white border-golf-orange' : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="px-4 flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="px-4 text-center py-16">
          <NewspaperIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No registrations found</p>
        </div>
      ) : (
        <div className="px-4 space-y-2">
          {filtered.map(reg => (
            <div key={reg.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-golf-gray text-sm truncate">{reg.full_name}</p>
                  <p className="text-xs text-gray-500 truncate">{reg.media_outlet}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-gray-400 capitalize">{reg.media_type}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-xs text-gray-400">{new Date(reg.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium capitalize ${statusColor(reg.status)}`}>
                    {statusIcon(reg.status)}
                    {reg.status}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-50">
                <button
                  onClick={() => setSelected(reg)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-golf-orange border border-golf-orange/30 rounded-xl"
                >
                  <EyeIcon className="w-4 h-4" />
                  View
                </button>
                <button
                  onClick={() => setDeleting(reg)}
                  className="flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium text-red-500 border border-red-100 rounded-xl"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <MobilePressDetailsSheet
          isOpen={!!selected}
          onClose={() => { setSelected(null); fetchRegistrations(); }}
          registration={selected}
        />
      )}

      <DeleteConfirmationModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Delete Registration"
        message={`Remove press registration for "${deleting?.full_name}"?`}
      />
    </div>
  );
}
